<template>
  <el-dialog title="批量设置目标值"
             :close-on-click-modal="false"
             width="600px"
             @closed="close"
             :visible.sync="visible"
             class="batch-dialog">
    <div class="upload-container">
      <!-- 下载模板 -->
      <div class="template-section">
        <el-button type="text"
                   icon="el-icon-download"
                   @click="downloadTemplate"
                   class="download-btn">
          下载Excel模板
        </el-button>
        <span class="template-tip">表头：实习生id、目标日期、目标值</span>
      </div>

      <!-- 上传区域 -->
      <div class="upload-area">
        <el-upload
          v-loading="uploadLoading"
          class="excel-upload"
          ref="excelUpload"
          drag
          action
          accept=".xlsx"
          :on-remove="excelFileRemove"
          :on-change="excelFileChange"
          :before-upload="beforeFileUpload"
          :http-request="uploadRequest">
          <div class="upload-content">
            <div class="upload-icon-wrapper">
              <i class="el-icon-upload2"></i>
            </div>
            <div class="upload-text">
              <p class="main-text">拖拽文件到此处或<em>点击上传</em></p>
              <p class="sub-text">仅支持 .xlsx 格式，最大 2MB</p>
            </div>
          </div>
        </el-upload>
      </div>

      <!-- 数据预览 -->
      <transition name="fade">
        <div v-if="excelData && excelData.length > 0" class="preview-section">
          <div class="preview-info">
            <i class="el-icon-success"></i>
            <span>已解析 {{ excelData.length }} 条数据</span>
          </div>
          <div class="preview-table">
            <el-table :data="excelData.slice(0, 3)"
                      size="small"
                      :show-header="true"
                      :border="false">
              <el-table-column prop="intern_user_id"
                               label="实习生ID"
                               width="100">
              </el-table-column>
              <el-table-column prop="plan_date"
                               label="目标日期"
                               width="120">
              </el-table-column>
              <el-table-column prop="plan_order_count"
                               label="目标值"
                               width="80">
              </el-table-column>
            </el-table>
            <div v-if="excelData.length > 3" class="preview-more">
              还有 {{ excelData.length - 3 }} 条数据...
            </div>
          </div>
        </div>
      </transition>
    </div>
    <span slot="footer" class="dialog-footer">
      <el-button @click="close()">取消</el-button>
      <el-button type="primary"
                 :disabled="!excelData || excelData.length === 0"
                 @click="onSubmitExcel()">
        提交{{ excelData && excelData.length > 0 ? ` (${excelData.length})` : '' }}
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import Worker from '@/worker/read-xlsx.worker'

const worker = new Worker()

export default {
  components: {},
  props: {
    projectId: {
      type: [String, Number],
      required: true,
    },
    auditProjectRangeId: {
      type: [String, Number],
      default: null,
    },
  },
  data () {
    return {
      visible: false,
      uploadLoading: false,
      excelData: [],
    }
  },
  computed: {},
  methods: {
    /**
     * 下载Excel模板
     */
    downloadTemplate () {
      this.$export('批量设置目标值模板', [
        {
          '实习生id': 1,
          '目标日期': '2024-01-01',
          '目标值': 10,
        },
        {
          '实习生id': 2,
          '目标日期': '2024-01-02',
          '目标值': 15,
        },
      ], {
        fileHeader: [
          {key: '实习生id', text: '实习生id'},
          {key: '目标日期', text: '目标日期'},
          {key: '目标值', text: '目标值'},
        ],
      })
    },
    /**
     * Excel文件移除
     */
    excelFileRemove () {
      this.excelData = []
    },
    /**
     * Excel文件变化
     */
    excelFileChange (file, fileList) {
      if (file.status === 'ready') {
        if (fileList.length >= 2) {
          fileList.shift()
        }
      }
    },
    /**
     * 上传前验证
     */
    beforeFileUpload (file) {
      const isLt2M = file.size / 1024 / 1024 < 2
      if (!isLt2M) {
        this.$message.error('选择文件大小不能超过 2MB!')
      }
      return isLt2M
    },
    /**
     * 上传请求
     */
    uploadRequest (obj) {
      this.uploadLoading = true
      worker.postMessage({file: obj.file, options: {header: 1}})
      worker.onmessage = (event) => {
        this.handleExcelData(event.data)
        this.uploadLoading = false
      }
    },
    /**
     * 格式化日期
     * 处理Excel中的各种日期格式：数字序列号、日期对象、字符串
     */
    formatDate (dateValue) {
      if (dateValue === null || dateValue === undefined || dateValue === '') {
        return ''
      }
      // 如果是Date对象
      if (dateValue instanceof Date) {
        if (!isNaN(dateValue.getTime())) {
          const year = dateValue.getFullYear()
          const month = String(dateValue.getMonth() + 1).padStart(2, '0')
          const day = String(dateValue.getDate()).padStart(2, '0')
          return `${year}-${month}-${day}`
        }
        return ''
      }
      // 如果是字符串
      if (typeof dateValue === 'string') {
        const trimmed = dateValue.trim()
        // 如果已经是 yyyy-MM-dd 格式，直接返回
        if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
          return trimmed
        }
        // 尝试解析其他格式的日期字符串（如 yyyy/MM/dd, MM/dd/yyyy 等）
        const date = new Date(trimmed)
        if (!isNaN(date.getTime())) {
          const year = date.getFullYear()
          const month = String(date.getMonth() + 1).padStart(2, '0')
          const day = String(date.getDate()).padStart(2, '0')
          return `${year}-${month}-${day}`
        }
        return trimmed
      }
      // 如果是数字（可能是Excel日期序列号）
      if (typeof dateValue === 'number') {
        // Excel日期序列号范围：1 到 2958465（对应1900-01-01到9999-12-31）
        // 如果数字在这个范围内，认为是Excel日期序列号
        if (dateValue >= 1 && dateValue <= 2958465) {
          // Excel 1900 日期系统：序列号 1 = 1900-01-01（序列号 n 表示从该日起的第 n 天）
          // 从 1899-12-30 加 n 天会少算一天，应从 1900-01-01 起加 (n - 1) 天
          const excelEpoch = new Date(1900, 0, 1)
          const date = new Date(excelEpoch.getTime() + (dateValue - 1) * 24 * 60 * 60 * 1000)
          if (!isNaN(date.getTime())) {
            const year = date.getFullYear()
            const month = String(date.getMonth() + 1).padStart(2, '0')
            const day = String(date.getDate()).padStart(2, '0')
            return `${year}-${month}-${day}`
          }
        }
        // 如果不是Excel日期序列号，可能是时间戳（毫秒）
        if (dateValue > 946684800000) { // 2000-01-01的时间戳
          const date = new Date(dateValue)
          if (!isNaN(date.getTime())) {
            const year = date.getFullYear()
            const month = String(date.getMonth() + 1).padStart(2, '0')
            const day = String(date.getDate()).padStart(2, '0')
            return `${year}-${month}-${day}`
          }
        }
      }
      return String(dateValue)
    },
    /**
     * 处理Excel数据
     */
    handleExcelData (rows) {
      const items = []
      const tableHeader = {
        intern_user_id: {
          index: null,
          text: '实习生id',
        },
        plan_date: {
          index: null,
          text: '目标日期',
        },
        plan_order_count: {
          index: null,
          text: '目标值',
        },
      }
      try {
        rows.forEach((row, rowIndex) => {
          const item = {}
          row.forEach((cell, cellIndex) => {
            if (rowIndex) {
              // 获得数据行前 检查表头是否存在
              if (rowIndex === 1) {
                for (const header of Object.values(tableHeader)) {
                  if (!Number.isInteger(header.index)) {
                    this.uploadLoading = false
                    this.$refs.excelUpload.clearFiles()
                    throw new Error(`找不到 [${header.text}] 表头`)
                  }
                }
              }
              // 表格的行数据获取
              for (const field in tableHeader) {
                if (tableHeader.hasOwnProperty(field)) {
                  // 根据字段的 index 获得相应的值
                  if (cellIndex === tableHeader[field].index) {
                    item[field] = cell
                  }
                }
              }
            } else {
              // 检查表头字段是否存在
              for (const header of Object.values(tableHeader)) {
                if (cell === header.text) {
                  header.index = cellIndex
                }
              }
            }
          })
          if (rowIndex) {
            items.push(item)
          }
        })
        // 转换为后端需要的格式
        this.excelData = items.map(item => {
          return {
            audit_project_range_id: this.auditProjectRangeId,
            intern_user_id: item.intern_user_id ? Number(item.intern_user_id) : null,
            plan_date: this.formatDate(item.plan_date),
            plan_order_count: item.plan_order_count ? Number(item.plan_order_count) : 0,
          }
        }).filter(item => {
          // 过滤掉无效数据
          return item.intern_user_id && item.plan_date && item.plan_order_count !== null
        })
        if (this.excelData.length === 0) {
          this.$message.warning('Excel文件中没有有效数据')
        } else {
          this.$message.success(`成功解析 ${this.excelData.length} 条数据`)
        }
      } catch (e) {
        this.$alert(e.toString(), '提示', {type: 'error'})
        this.excelData = []
      }
    },
    /**
     * 提交Excel数据
     */
    onSubmitExcel () {
      if (!this.excelData || this.excelData.length === 0) {
        this.$message.error('请先上传Excel文件')
        return
      }
      if (!this.auditProjectRangeId) {
        this.$message.error('缺少audit_project_range_id参数')
        return
      }
      request.common('batchCreateTargetValue', this.excelData).then((response) => {
        const data = response.data || {}
        const created = data.created || 0
        const updated = data.updated || 0
        const skipped = data.skipped || 0

        let message = '批量操作完成：'
        const parts = []
        if (created > 0) {
          parts.push(`新增 ${created} 条`)
        }
        if (updated > 0) {
          parts.push(`更新 ${updated} 条`)
        }
        if (skipped > 0) {
          parts.push(`跳过 ${skipped} 条`)
        }
        if (parts.length > 0) {
          message += parts.join('，')
        } else {
          message = '批量操作完成'
        }

        this.$message.success(message)
        this.$emit('submitted')
        this.close()
      }).catch(() => {
        this.$message.error('批量创建失败')
      })
    },
    show () {
      Object.assign(this.$data, this.$options.data.call(this))
      this.visible = true
    },
    close () {
      this.visible = false
      if (this.$refs.excelUpload) {
        this.$refs.excelUpload.clearFiles()
      }
      Object.assign(this.$data, this.$options.data.call(this))
    },
  },
}
</script>

<style lang="scss" scoped>
.upload-container {
  padding: 8px 0;
}

.template-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;

  .download-btn {
    padding: 0;
    color: #409eff;
    font-size: 14px;

    &:hover {
      color: #66b1ff;
    }

    i {
      margin-right: 4px;
    }
  }

  .template-tip {
    font-size: 12px;
    color: #909399;
  }
}

.upload-area {
  margin-bottom: 20px;

  .excel-upload {
    width: 100%;

    ::v-deep .el-upload-dragger {
      width: 100%;
      height: 180px;
      border: 1.5px dashed #dcdfe6;
      border-radius: 6px;
      background: #fafbfc;
      transition: all 0.2s ease;

      &:hover {
        border-color: #409eff;
        background: #f5f9ff;
      }

      .upload-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 100%;
        padding: 20px;

        .upload-icon-wrapper {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          background: #f0f2f5;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 16px;
          transition: all 0.2s ease;

          i {
            font-size: 32px;
            color: #909399;
            transition: all 0.2s ease;
          }
        }

        .upload-text {
          text-align: center;

          .main-text {
            font-size: 15px;
            color: #606266;
            margin: 0 0 6px 0;
            font-weight: 500;

            em {
              color: #409eff;
              font-style: normal;
              cursor: pointer;
            }
          }

          .sub-text {
            font-size: 12px;
            color: #909399;
            margin: 0;
          }
        }
      }
    }

    ::v-deep .el-upload-dragger:hover {
      .upload-icon-wrapper {
        background: #e6f4ff;

        i {
          color: #409eff;
          transform: translateY(-2px);
        }
      }
    }
  }
}

.preview-section {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;

  .preview-info {
    display: flex;
    align-items: center;
    margin-bottom: 12px;
    font-size: 13px;
    color: #67c23a;

    i {
      margin-right: 6px;
      font-size: 16px;
    }
  }

  .preview-table {
    background: #fafbfc;
    border-radius: 4px;
    padding: 12px;
    overflow: hidden;

    ::v-deep .el-table {
      background: transparent;

      &::before {
        display: none;
      }

      th {
        background: transparent;
        border: none;
        padding: 8px 0;
        font-size: 12px;
        color: #909399;
        font-weight: 500;
      }

      td {
        border: none;
        padding: 8px 0;
        font-size: 13px;
        color: #606266;
      }

      tr {
        background: transparent;

        &:hover {
          background: transparent;
        }
      }
    }

    .preview-more {
      text-align: center;
      padding-top: 8px;
      font-size: 12px;
      color: #909399;
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.dialog-footer {
  .el-button {
    min-width: 90px;
  }
}

::v-deep .batch-dialog {
  .el-dialog__body {
    padding: 20px;
  }
}
</style>




// WEBPACK FOOTER //
// src/views/order-review-target-management/components/BatchTargetDialog.vue